import Vue from 'vue';
import Router from 'vue-router';
import Meta from 'vue-meta';
import { sync } from 'vuex-router-sync';

import { initializeAfterEach, closeModals, setContent, setStyleTemplate } from './guards/afterEach';
import {
	initializeBeforeEach,
	legacyUrlQuery,
	loadDataSourceCFG,
	initializeAnalytics,
	identifyHistoryAction,
} from './guards/beforeEach';
import {
	initializeBeforeResolve,
	inferRouteChangedByBrowser,
	startAR,
	doAnalytics,
} from './guards/beforeResolve';
import {
	initializeBeforeEachValidation,
	validateExhibition,
	validateTour,
	validateArticle,
	validateArticleInTour,
	validateStayInTour,
} from './guards/beforeEachValidation';

// import beforeValidateURL from './guards/beforeEach/beforeValidateURL';

Vue.use(Router);
Vue.use(Meta);
// Navigation router sequence
// 1. Navigation triggered.
// 2. Call beforeRouteLeave guards in deactivated components.
// 3. Call global beforeEach guards.
// 4. Call beforeRouteUpdate guards in reused components.
// 5. Call beforeEnter in route configs.
// 6. Resolve async route components.
// 7. Call beforeRouteEnter in activated components.
// 8. Call global beforeResolve guards.
// 9. Navigation confirmed.
// 10. Call global afterEach hooks.
// 11. DOM updates triggered.

function mergeGuards(guards) {
	return async function (to, from, next) {
		for (const guard of guards) {
			const result = await new Promise((resolve) => {
				guard(to, from, resolve);
			});
			if (result) {
				return next(result);
			}
		}
		return next();
	};
}

export let router;
// To prevent circular dependencies the initialize function takes the
export function initializeRouter(store) {
	router = new Router({
		// mode: 'history',
		base: process.env.BASE_URL,
		routes: [
			{
				// open tour article
				path: '/:exhibitionID/tour/:tourID/:articleID',
				name: 'contentTour',
			},
			{
				// start tourtour-
				path: '/:exhibitionID/tour/:tourID',
				name: 'redirectToContentTour',
				beforeEnter: (to, from, next) => {
					const { exhibitionID, tourID } = to.params;
					const { exhibitIDs } = store.state.tour.toursMap[to.params.tourID];
					next({
						path: `/${exhibitionID}/tour/${tourID}/${exhibitIDs[0] && exhibitIDs[0].articleID}`,
						query: to.query,
						replace: true,
					});
				},
			},
			{
				path: '/:exhibitionID/:articleID',
				name: 'content',
				beforeEnter: mergeGuards([validateStayInTour, validateExhibition, validateArticle]),
			},
			{
				path: '/:exhibitionID',
				name: 'redirectToContent',
				beforeEnter: (to, from, next) => {
					const { exhibitionID, landingArticleID } =
						store.state.dataSourceCFG.exhibitionMap[to.params.exhibitionID];
					next({
						path: `/${exhibitionID}/${landingArticleID}`,
						query: to.query,
						replace: true,
					});
				},
			},

			{
				path: '*', // Redirect all broken urls
				beforeEnter: (to, from, next) => {
					const { landingExhibition } = store.state.dataSourceCFG;
					next({
						path: `/${landingExhibition.exhibitionID}/${landingExhibition.landingArticleID}`,
						query: to.query,
						replace: true,
					});
				},
			},
		],
	});
	initializeAfterEach(store);
	initializeBeforeEach(store, router);
	initializeBeforeResolve(store);
	initializeBeforeEachValidation(store);

	// Sync router to store so router is part of the store.
	sync(store, router);

	// Guards will be execute in order of the array
	[
		legacyUrlQuery,
		loadDataSourceCFG,
		initializeAnalytics,
		identifyHistoryAction,
		validateExhibition,
		validateTour,
		validateArticle,
		validateArticleInTour,
	].forEach((guard) => {
		router.beforeEach(guard);
	});

	[inferRouteChangedByBrowser, doAnalytics, startAR].forEach((guard) => {
		router.beforeResolve(guard);
	});

	// // Guards will bexecuted in parrallel
	[closeModals, setStyleTemplate, setContent].forEach((guard) => {
		router.afterEach(guard);
	});

	return router;
}
